import {
  TriggerDatasetType,
  TriggerInputDataset,
} from '@/hooks/api/datasets/trigger/useTriggerManualRun.ts';
import { ManualTriggerInput } from '@/types/automationSteps.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import ManualTriggerInputHeader from './ManualTriggerInputHeader.tsx';
import PreviouslyUploadedFiles from '../PreviouslyUploadedFiles/PreviouslyUploadedFiles.tsx';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import { Divider, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';

type Props = {
  automationId: string;
  trigger: ManualTriggerInput;
  triggerDataset: TriggerInputDataset;
  setTriggerDataset: (dataset: TriggerInputDataset) => void;
  isLoading: boolean;
};

const JsonDatasetInput = ({
  automationId,
  trigger,
  triggerDataset,
  setTriggerDataset,
  isLoading,
}: Props) => {
  const [enterNewJson, setEnterNewJson] = useState(true);

  useEffect(() => {
    setEnterNewJson(triggerDataset.type !== TriggerDatasetType.Existing);
  }, [triggerDataset.type]);

  const text = triggerDataset.type === TriggerDatasetType.Json ? triggerDataset.text : '';
  const selectedExistingDatasetId =
    triggerDataset.type === TriggerDatasetType.Existing ? triggerDataset.dataset_id : undefined;
  let error: string | undefined;
  if (text.trim()) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        error = 'Enter a JSON object.';
      }
    } catch {
      error = 'Enter valid JSON.';
    }
  }

  return (
    <>
      <Divider style={{ color: '#ececec', borderTopWidth: '0.5px' }} />
      <Stack gap={0}>
        <CustomLoadingOverlay visible={isLoading} />
        <ManualTriggerInputHeader
          trigger={trigger}
          actionLabel={enterNewJson ? 'or use previous JSON input' : 'or enter new JSON'}
          onActionClick={() => setEnterNewJson(!enterNewJson)}
        />
        {enterNewJson ? (
          <StyledTextArea
            label={
              trigger.schema_ref
                ? `${trigger.schema_ref.schema_id}.v${trigger.schema_ref.version}`
                : 'JSON object'
            }
            value={text}
            disabled={isLoading}
            minRows={8}
            error={error}
            onChange={(event) =>
              setTriggerDataset({
                trigger_id: trigger.output_id,
                type: TriggerDatasetType.Json,
                text: event.currentTarget.value,
              })
            }
          />
        ) : (
          <PreviouslyUploadedFiles
            automationId={automationId}
            manualTrigger={trigger}
            selectedExistingDatasetId={selectedExistingDatasetId}
            setSelectedExistingDatasetId={(dataset_id) =>
              setTriggerDataset({
                trigger_id: trigger.output_id,
                type: TriggerDatasetType.Existing,
                dataset_id,
              })
            }
          />
        )}
      </Stack>
    </>
  );
};

export default JsonDatasetInput;
